/**
 * Set selector for placeholder products that group two real products' SKUs.
 *
 * The set product is a placeholder: it holds the set price while the SKUs that ship come from two
 * roles, primary and secondary, each a list of colour products on the placeholder's metafields. For
 * the launch set the primaries are full divvies and the secondaries half divvies. Every swatch
 * carries the variant ids for both of its roles, so the two modes share one colour list:
 *
 * - single: adds one primary as an ordinary cart line at its own price.
 * - set: adds the primary and secondary at their configured quantities, all sharing a `_bundle_id`
 *   so the Cart Transform merges them into a single line priced at the placeholder's price. The
 *   property contract matches `bundles.js`, which the same function already handles.
 *
 * The gallery follows the same selection. `divvy-set-gallery.liquid` renders every colour's images
 * tagged by colour and mode, so this only names the key to show.
 */

const DIVVY_SET_ADDED_LABEL_DURATION = 3000;

/**
 * @param {number} cents
 * @returns {string}
 */
const divvySetMoney = (cents) => {
  if (typeof window.formatProductPriceNoZeros === 'function') {
    return window.formatProductPriceNoZeros(cents);
  }

  return `$${(Math.round(cents) / 100).toFixed(2)}`;
};

/**
 * @typedef {Object} DivvySetPrices
 * @property {number} price - Price charged for the current selection, in cents.
 * @property {number} compare - Value to strike through, in cents. Equal to price when not on sale.
 */

class DivvySetSelector extends HTMLElement {
  connectedCallback() {
    this.modeInputs = Array.from(this.querySelectorAll('input[name^="divvy-set-mode"]'));
    this.colorInputs = Array.from(this.querySelectorAll('input[name^="divvy-set-color"]'));
    this.modeLabel = this.querySelector('[data-divvy-set-mode-label]');
    this.colorLabel = this.querySelector('[data-divvy-set-color-label]');
    this.singleThumb = this.querySelector('[data-divvy-set-mode-thumb="single"]');
    this.setThumb = this.querySelector('[data-divvy-set-mode-thumb="set"]');
    this.note = this.querySelector('[data-divvy-set-note]');
    this.priceOutput = this.querySelector('[data-divvy-set-atc-price]');
    this.atcLabel = this.querySelector('[data-divvy-set-atc-label]');
    this.buyButton = this.querySelector('divvy-set-buy-button button');

    this.setHandle = this.dataset.setHandle;
    this.setVariantId = this.dataset.setVariantId;
    this.setPrice = Number(this.dataset.setPrice);
    this.primaryQuantity = Number(this.dataset.primaryQuantity) || 2;
    this.secondaryQuantity = Number(this.dataset.secondaryQuantity) || 1;
    this.addLabel = this.atcLabel ? this.atcLabel.textContent.trim() : 'Add to Bag';

    this.handleChange = this.handleChange.bind(this);

    for (const input of [...this.modeInputs, ...this.colorInputs]) {
      input.addEventListener('change', this.handleChange);
    }

    this.refresh();
  }

  disconnectedCallback() {
    for (const input of [...this.modeInputs, ...this.colorInputs]) {
      input.removeEventListener('change', this.handleChange);
    }

    clearTimeout(this.labelResetTimeout);
  }

  handleChange() {
    clearTimeout(this.labelResetTimeout);
    this.refresh();
  }

  /** @returns {string} Either 'single' or 'set'. */
  get mode() {
    const checked = this.modeInputs.find((input) => input.checked);
    return checked ? checked.value : 'set';
  }

  /** @returns {HTMLInputElement|null} */
  get selectedColor() {
    return this.colorInputs.find((input) => input.checked) || null;
  }

  /** @returns {boolean} Whether the current selection can be added to the cart. */
  get sellable() {
    const input = this.selectedColor;
    if (!input) return false;

    return this.isSellable(input, this.mode);
  }

  /**
   * A colour needs the primary in stock for single mode, and both pieces in stock for set mode.
   * @param {HTMLInputElement} input
   * @param {string} mode
   * @returns {boolean}
   */
  isSellable(input, mode) {
    const primaryAvailable = input.dataset.primaryAvailable === 'true';
    if (mode === 'single') return primaryAvailable;

    const secondaryAvailable = input.dataset.secondaryAvailable === 'true';
    return primaryAvailable && secondaryAvailable && input.dataset.secondaryVariantId !== '';
  }

  /** Re-evaluates swatch availability, pricing and the buy button for the current selection. */
  refresh() {
    const mode = this.mode;

    for (const input of this.colorInputs) {
      input.classList.toggle('out-of-stock', !this.isSellable(input, mode));
      this.syncSwatchImage(input, mode);
    }

    if (!this.selectedColor) {
      const firstSellable = this.colorInputs.find((input) => this.isSellable(input, mode));
      const fallback = firstSellable || this.colorInputs[0];
      if (fallback) fallback.checked = true;
    }

    const input = this.selectedColor;
    if (!input) return;

    if (this.modeLabel) {
      this.modeLabel.textContent = mode === 'single' ? this.dataset.singleLabel : this.dataset.setLabel;
    }

    if (this.colorLabel) {
      this.colorLabel.textContent = input.dataset.colorName;
    }

    this.updateThumbSrc(this.singleThumb, input.dataset.primaryImage);
    this.updateThumbSrc(this.setThumb, input.dataset.setImage);

    this.updateNote(input, mode);
    this.updatePrices(this.prices(input, mode));
    this.updateBuyButton();
    this.syncGallery(input, mode);
  }

  /**
   * Points one swatch at the imagery for the mode: the colour's own product image for a single
   * panel, the first of its set shots for the set. A colour with no set imagery carries the same URL
   * in both attributes, so its swatch simply holds still.
   *
   * @param {HTMLInputElement} input
   * @param {string} mode
   */
  syncSwatchImage(input, mode) {
    const label = this.querySelector(`label[for="${input.id}"]`);
    const thumb = label?.querySelector('[data-divvy-set-color-thumb]');
    const src = mode === 'set' ? input.dataset.swatchSetImage : input.dataset.swatchSingleImage;

    this.updateThumbSrc(thumb, src);
  }

  /**
   * Compared on the attribute rather than the property, because `image_url` renders a
   * protocol-relative URL while the property reads back absolute, so a property comparison never
   * matches and would rewrite the src on every refresh.
   *
   * @param {HTMLImageElement|null|undefined} img
   * @param {string|undefined} src
   */
  updateThumbSrc(img, src) {
    if (!img || !src || img.getAttribute('src') === src) return;

    img.setAttribute('src', src);
  }

  /**
   * Points the gallery and the zoom modal at the images for the current selection.
   *
   * Both hold every colour's media, tagged with a data-color key: the colour handle for single mode,
   * the handle plus '--set' for set mode.
   *
   * @param {HTMLInputElement} input
   * @param {string} mode
   */
  syncGallery(input, mode) {
    const color = input.dataset.colorHandle;
    if (!color) return;

    const showSetImages = mode === 'set' && input.dataset.hasSetImages === 'true';
    const key = showSetImages ? `${color}--set` : color;

    for (const gallery of document.querySelectorAll(`[id^="MediaGallery-${this.dataset.sectionId}"]`)) {
      this.showGalleryKey(gallery, key);
    }

    this.filterModal(key);
  }

  /**
   * Swaps the gallery over to one colour and mode.
   *
   * Hiding the other colours with a class is not enough. Swiper sizes every slide it owns from the
   * container width, so a slide it has been told to hide still holds its place in Swiper's grid while
   * taking no space on screen. Paging then works off positions that are not there, which is why it
   * landed on the last image and why the arrows sprang straight back to it. Taking the other colours
   * out of the wrapper instead leaves Swiper measuring exactly what is on screen, so its own paging,
   * arrows and pagination all work untouched.
   *
   * The colours that are off screen are held per key and swapped back in on the next change. Images
   * inside a detached node never load, so carrying every colour in the page costs nothing until it
   * is asked for.
   *
   * @param {HTMLElement} gallery
   * @param {string} key
   */
  showGalleryKey(gallery, key) {
    const wrapper = gallery.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const media = this.galleryMedia(gallery, wrapper);
    const slides = media.slides.get(key) || media.slides.get(key.replace(/--set$/, ''));
    if (!slides || slides.length === 0) return;

    const thumbs = media.thumbs.get(key) || media.thumbs.get(key.replace(/--set$/, '')) || [];

    // Torn down and built again rather than updated in place. On mobile the gallery loops, and
    // Swiper's looping owns the order of the slides it was built with, so handing it a different set
    // of children is not something an update can be trusted to take in.
    const hadSlider = Boolean(gallery.slider);
    if (hadSlider) {
      gallery.slider.destroy(true, true);
      gallery.slider = null;
    }

    wrapper.replaceChildren(...slides);
    slides.forEach((slide, index) => {
      slide.classList.remove('is-hidden');
      slide.classList.toggle('is-active', index === 0);
    });

    if (media.thumbList) {
      media.thumbList.replaceChildren(...thumbs);
      thumbs.forEach((thumb, index) => {
        thumb.classList.remove('is-hidden');
        const button = thumb.querySelector('button');
        if (!button) return;

        if (index === 0) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      // The strip's layout and its scroll arrows are rendered from the number of images the colour
      // on load happens to have, and the colours do not all carry the same number.
      const strip = gallery.querySelector('[id^="GalleryThumbnails"]');
      strip?.classList.toggle('thumbnail-slider--no-slide', thumbs.length <= 3);

      for (const button of strip?.querySelectorAll('.slider-button') || []) {
        button.classList.toggle('small-hide', thumbs.length <= 3);
        button.classList.toggle('medium-hide', thumbs.length <= 4);
        button.classList.toggle('large-up-hide', thumbs.length <= 4);
      }
    }

    // Set before the event so a gallery that has not been upgraded yet reads the key in its own
    // connectedCallback. The event covers the housekeeping around the swap: the image counter, the
    // active thumbnail and the thumbnail strip's own scroll position and arrows.
    gallery.dataset.activeColor = key;
    gallery.dataset.totalMediaCount = String(slides.length);
    gallery.dispatchEvent(new CustomEvent('colorChanged', { detail: { color: key } }));

    if (hadSlider) gallery.initSlider?.();
  }

  /**
   * Groups the gallery's slides and thumbnails by data-color, once per gallery. Runs before
   * media-gallery.js has upgraded on first load, so the grouping sees the markup as rendered rather
   * than anything Swiper has rearranged.
   *
   * @param {HTMLElement} gallery
   * @param {HTMLElement} wrapper
   */
  galleryMedia(gallery, wrapper) {
    if (gallery.divvySetMedia) return gallery.divvySetMedia;

    const groupByColor = (nodes) => {
      const grouped = new Map();

      for (const node of nodes) {
        const nodeKey = node.dataset.color;
        if (!nodeKey) continue;

        if (!grouped.has(nodeKey)) grouped.set(nodeKey, []);
        grouped.get(nodeKey).push(node);
      }

      return grouped;
    };

    const thumbList = gallery.querySelector('.thumbnail-list');

    gallery.divvySetMedia = {
      thumbList,
      slides: groupByColor(wrapper.querySelectorAll(':scope > li[data-color]')),
      thumbs: groupByColor(thumbList ? thumbList.querySelectorAll(':scope > li[data-color]') : [])
    };

    return gallery.divvySetMedia;
  }

  /** @param {string} key */
  filterModal(key) {
    const modal = document.getElementById(`ProductModal-${this.dataset.sectionId}`);
    if (!modal) return;

    for (const item of modal.querySelectorAll('[data-color]')) {
      item.classList.toggle('divvy-set-modal__item--hidden', item.dataset.color !== key);
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} mode
   * @returns {DivvySetPrices}
   */
  prices(input, mode) {
    if (mode === 'single') {
      return {
        price: Number(input.dataset.primaryPrice),
        compare: Number(input.dataset.primaryComparePrice)
      };
    }

    return {
      price: this.setPrice,
      compare: Number(input.dataset.setValue)
    };
  }

  /**
   * Explains why a colour cannot be added, so the disabled button is never unexplained.
   * @param {HTMLInputElement} input
   * @param {string} mode
   */
  updateNote(input, mode) {
    if (!this.note) return;

    const primaryAvailable = input.dataset.primaryAvailable === 'true';
    const hasSecondary = input.dataset.secondaryVariantId !== '';
    const secondaryAvailable = input.dataset.secondaryAvailable === 'true';

    if (!primaryAvailable) {
      this.note.textContent = this.dataset.noteSoldOut;
      return;
    }

    if (mode === 'single') {
      this.note.textContent = '';
      return;
    }

    if (!hasSecondary) {
      this.note.textContent = this.dataset.noteSingleOnly;
      return;
    }

    this.note.textContent = secondaryAvailable ? '' : this.dataset.noteSoldOutAsSet;
  }

  /**
   * The PDP renders the price twice: once in the title block and once in the price block.
   * @returns {HTMLElement[]}
   */
  get priceWrappers() {
    const inTitle = Array.from(document.querySelectorAll('.product__title > .price'));
    const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
    const inBlock = priceBlock ? Array.from(priceBlock.querySelectorAll('.price')) : [];

    return [...inTitle, ...inBlock];
  }

  /**
   * Updates the product price displays and the price shown inside the buy button.
   * @param {DivvySetPrices} prices
   */
  updatePrices({ price, compare }) {
    const onSale = compare > price;

    for (const priceWrapper of this.priceWrappers) {
      const regular = priceWrapper.querySelector('.price__regular .price-item--regular');
      const saleCompare = priceWrapper.querySelector('.price__sale s.price-item--regular');
      const sale = priceWrapper.querySelector('.price__sale .price-item--sale');

      priceWrapper.classList.toggle('price--on-sale', onSale);

      if (onSale && saleCompare && sale) {
        saleCompare.textContent = divvySetMoney(compare);
        sale.textContent = divvySetMoney(price);
      } else if (regular) {
        regular.textContent = divvySetMoney(price);
      }
    }

    if (!this.priceOutput) return;

    this.priceOutput.textContent = onSale ? `${divvySetMoney(price)} ` : divvySetMoney(price);

    if (!onSale) return;

    const valueLabel = document.createElement('span');
    valueLabel.textContent = (this.dataset.valueLabel || '(__price__ Value)').replace(
      '__price__',
      divvySetMoney(compare)
    );
    this.priceOutput.appendChild(valueLabel);
  }

  /** Locks the buy button when the selection is not purchasable. */
  updateBuyButton() {
    if (!this.buyButton) return;

    const sellable = this.sellable;

    this.buyButton.disabled = !sellable;
    this.buyButton.setAttribute('aria-disabled', String(!sellable));

    if (this.atcLabel) {
      this.atcLabel.textContent = sellable ? this.addLabel : this.dataset.soldOutLabel;
    }

    if (this.priceOutput) {
      this.priceOutput.hidden = !sellable;
    }
  }

  /**
   * Shows transient feedback on the button, then restores its normal state.
   * @param {string} label
   */
  flashLabel(label) {
    if (!this.atcLabel) return;

    this.atcLabel.textContent = label;
    if (this.priceOutput) this.priceOutput.hidden = true;

    this.labelResetTimeout = setTimeout(() => this.refresh(), DIVVY_SET_ADDED_LABEL_DURATION);
  }

  /**
   * Puts a cart error in the note, which is a live region, so the failure is announced rather than
   * only implied by the button label.
   * @param {unknown} error
   */
  showError(error) {
    if (!this.note || typeof error !== 'string' || error === '') return;

    this.note.textContent = error;
  }

  /**
   * Builds the `/cart/add.js` payload for the current selection.
   * @returns {Array<Object>|null} Line items, or null when nothing is purchasable.
   */
  buildCartItems() {
    const input = this.selectedColor;
    if (!input) return null;

    const mode = this.mode;
    if (!this.isSellable(input, mode)) return null;

    const primaryVariantId = input.dataset.primaryVariantId;

    if (mode === 'single') {
      return [{ id: primaryVariantId, quantity: 1 }];
    }

    const componentValue = Number(input.dataset.setValue);
    const discountPercentage =
      componentValue > this.setPrice
        ? Math.round(((componentValue - this.setPrice) / componentValue) * 100)
        : 0;

    // One `_bundle_id` per add groups the lines for the Cart Transform and for grouped removal.
    const bundleId = `${Number(this.setVariantId)}|${Date.now()}`;
    const variantIds = [
      ...Array(this.primaryQuantity).fill(primaryVariantId),
      ...Array(this.secondaryQuantity).fill(input.dataset.secondaryVariantId)
    ];

    // Each piece is posted as its own quantity-1 line so the function sees the same shape as bundles.
    return variantIds.map((id, index) => ({
      id,
      quantity: 1,
      properties: {
        _id: `${bundleId}-${index}`,
        _bundle_handle: this.setHandle,
        _bundle_id: bundleId,
        _bundle_variant_id: Number(this.setVariantId),
        _bundle_items: variantIds.join('|'),
        _bundle_price: componentValue,
        _bundle_discount_percentage: discountPercentage,
        _bundle_final_price: this.setPrice
      }
    }));
  }
}

customElements.define('divvy-set-selector', DivvySetSelector);

class DivvySetBuyButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.spinner = this.querySelector('.loading-overlay__spinner');
    this.loading = false;
    this.handleClick = this.handleClick.bind(this);

    this.button?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  /**
   * @param {MouseEvent} event
   */
  async handleClick(event) {
    event.preventDefault();

    if (this.loading) return;

    const selector = this.closest('divvy-set-selector');
    if (!selector) return;

    const items = selector.buildCartItems();
    if (!items) return;

    if (typeof window.theme?.addToCart !== 'function') {
      console.error('Divvy set add to cart error: cart store is not available');
      return;
    }

    this.setLoading(true);

    try {
      const result = await window.theme.addToCart(items);

      if (result && result.error) {
        selector.showError(result.error);
        selector.flashLabel(selector.dataset.retryLabel);
        return;
      }

      selector.flashLabel(selector.dataset.addedLabel);
    } catch (error) {
      console.error('Divvy set add to cart error:', error);
      selector.flashLabel(selector.dataset.retryLabel);
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * @param {boolean} loading
   */
  setLoading(loading) {
    this.loading = loading;

    if (!this.button) return;

    this.button.classList.toggle('loading', loading);
    this.spinner?.classList.toggle('hidden', !loading);

    if (loading) {
      this.button.disabled = true;
      this.button.setAttribute('aria-disabled', 'true');
      return;
    }

    // Restores the selection's own state rather than simply enabling, so a sold out colour stays
    // locked, and leaves the label alone so the transient feedback survives.
    const sellable = this.closest('divvy-set-selector')?.sellable ?? true;

    this.button.disabled = !sellable;
    this.button.setAttribute('aria-disabled', String(!sellable));
  }
}

customElements.define('divvy-set-buy-button', DivvySetBuyButton);
